import { useEffect } from 'react'
import { useWebMidi } from '@/hooks/useWebMidi'
import { useAudioEngine } from '@/hooks/useAudioEngine'
import { useMidiStore } from '@/stores/midiStore'
import {
  useSequencerStore,
  selectCurrentContainer,
  selectCurrentProject,
  PADS_PER_PROJECT,
} from '@/stores/sequencerStore'
import { stepPositionAt } from '@/audio/playbackTracker'

/**
 * Phase 3c — capture live MIDI note-ons into the active pattern
 * while record mode is armed.
 *
 * Resolves each incoming note to a pad index using the same
 * precedence rules as live pad triggering:
 *   1. Explicit `padNoteBindings` entry (set via MIDI Learn) wins.
 *   2. Chromatic-by-pad default — pad i is triggered by note 48+i.
 * Unmapped notes are ignored (same as the live-trigger path).
 *
 * Timing is read from `playbackTracker.stepPositionAt(currentTime)`;
 * the scheduler's `onStep` calls `markStep` at each step boundary
 * with the audio-clock time, so a fresh note lands at the exact
 * fractional position between the last-fired step and now.
 *
 * Record mode semantics:
 *   - `record`  — overwrite the cell at (row, stepIndex).
 *   - `overdub` — preserve existing rich-cell fields (fx, envelope,
 *                 length, roll) and only update velocity +
 *                 microOffset. Lets the user layer several passes
 *                 of timing / velocity feel over the same steps.
 *
 * Mounted once at the root (`PlaybackProvider`) so it records
 * regardless of which route is active.
 */
export function useNoteRecording(): void {
  const { subscribeSequencerNoteIn } = useWebMidi()
  // Timing reference comes from the audio engine's AudioContext so
  // the step-position read stays in the same clock domain that
  // `markStep` writes from. `useWebMidi`'s timestamps are
  // DOMHighResTimeStamp-based, which would drift against the
  // scheduler's audio-time mark.
  const { currentTime } = useAudioEngine()
  const padNoteBindings = useMidiStore(s => s.padNoteBindings)

  useEffect(() => {
    const unsubscribe = subscribeSequencerNoteIn(ev => {
      if (!ev.isNoteOn) return

      // Read store state on each event rather than via hook
      // selectors, so the subscription lives independent of render
      // cycles and we avoid rebuilding the callback whenever
      // transport state changes.
      const s = useSequencerStore.getState()
      if (s.recordMode === 'off') return
      if (!s.isPlaying) return

      const position = stepPositionAt(currentTime())
      if (!position) return
      const { stepIndex, microOffset } = position

      // ── Resolve target pad ──
      // (1) Learn-bound notes win. `padNoteBindings` maps
      //     padIndex → note, so reverse-scan for the match.
      let padIndex: number | null = null
      for (const [idx, bound] of Object.entries(padNoteBindings)) {
        if (bound === ev.note) {
          padIndex = Number(idx)
          break
        }
      }
      // (2) Chromatic-by-pad fallback — pad i is note 48+i. Skip
      //     pads that have a learn binding (so the override layer
      //     stays authoritative) and skip out-of-range notes.
      if (padIndex === null) {
        const chromaticIdx = ev.note - 48
        if (
          chromaticIdx >= 0 &&
          chromaticIdx < PADS_PER_PROJECT &&
          padNoteBindings[chromaticIdx] === undefined
        ) {
          padIndex = chromaticIdx
        }
      }

      if (padIndex === null) {
        // (3) Try custom MIDI overflow pads. Recording into a MIDI
        //     overflow pad writes against that pad's own step row.
        const project = selectCurrentProject(s)
        const midiPad = project?.midiPads.find(
          mp => mp.note === ev.note && mp.channel === ev.channel,
        )
        if (!midiPad) return
        s.recordCell({ kind: 'midi', padId: midiPad.id }, stepIndex, {
          microOffset,
          velocity: ev.velocity,
          overdub: s.recordMode === 'overdub',
        })
        return
      }

      // Guard: container / bank must resolve. If the project is
      // mid-switch this can be null for a frame — drop the note
      // rather than writing into a stale bank.
      const container = selectCurrentContainer(s)
      if (!container) return

      s.recordCell({ kind: 'kit', padIndex }, stepIndex, {
        microOffset,
        velocity: ev.velocity,
        overdub: s.recordMode === 'overdub',
      })
    })
    return unsubscribe
  }, [subscribeSequencerNoteIn, currentTime, padNoteBindings])
}
